import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { ThemeLogo } from './ThemeLogo';
import { api } from '../lib/api';

export const Footer = () => {
    const { isAuthenticated } = useAuth();
    const { isDark } = useTheme();
    const currentYear = new Date().getFullYear();
    const [apps, setApps] = useState([]);
    const [settings, setSettings] = useState({});

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [appsData, settingsData] = await Promise.all([
                    api.getApplications().catch(() => []),
                    api.getSettings().catch(() => ({}))
                ]);
                setApps(appsData.slice(0, 4));
                setSettings(settingsData);
            } catch (e) { /* ignore */ }
        };
        fetchData();
    }, []);

    return (
        <footer className={`border-t border-border relative z-10 ${isDark ? 'bg-slate-950' : 'bg-gray-50'}`} data-testid="footer" role="contentinfo">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                    <div className="md:col-span-2">
                        <Link to="/" className="flex items-center gap-2 mb-4" aria-label="MacTech Ana Sayfa">
                            <ThemeLogo className="h-10" />
                        </Link>
                        <p className="text-gray-400 text-sm max-w-md">
                            {settings.footer_slogan || 'Teknoloji \u00B7 Güven \u00B7 Çözüm'}
                        </p>
                        <p className="text-gray-500 text-xs mt-2 max-w-md">
                            {settings.footer_description || 'Farklı sektörler için özel olarak geliştirilen yazılım çözümleri sunan teknoloji platformu.'}
                        </p>
                    </div>

                    <nav aria-label="Hızlı Bağlantılar">
                        <h4 className="text-foreground font-medium mb-4">Hizli Baglantilar</h4>
                        <ul className="space-y-2">
                            <li><Link to="/" className="text-muted-foreground hover:text-foreground text-sm transition-colors">Ana Sayfa</Link></li>
                            <li><Link to="/uygulamalar" className="text-muted-foreground hover:text-foreground text-sm transition-colors">Uygulamalar</Link></li>
                            <li><Link to="/fiyatlandirma" className="text-muted-foreground hover:text-foreground text-sm transition-colors">Fiyatlandirma</Link></li>
                            <li><Link to="/nasil-calisir" className="text-muted-foreground hover:text-foreground text-sm transition-colors">Nasil Calisir?</Link></li>
                            <li><Link to="/hakkimizda" className="text-muted-foreground hover:text-foreground text-sm transition-colors">Hakkimizda</Link></li>
                            <li><Link to="/iletisim" className="text-muted-foreground hover:text-foreground text-sm transition-colors">Iletisim</Link></li>
                            <li><Link to="/sss" className="text-muted-foreground hover:text-foreground text-sm transition-colors">SSS</Link></li>
                            <li>
                                {isAuthenticated ? (
                                    <Link to="/dashboard" className="text-muted-foreground hover:text-foreground text-sm transition-colors">Dashboard</Link>
                                ) : (
                                    <Link to="/giris" className="text-muted-foreground hover:text-foreground text-sm transition-colors">Giris Yap</Link>
                                )}
                            </li>
                        </ul>
                    </nav>

                    <div>
                        <h4 className="text-foreground font-medium mb-4">Sektorler</h4>
                        <ul className="space-y-2">
                            {apps.length > 0 ? apps.map(app => (
                                <li key={app.id}>
                                    <Link to={`/uygulama/${app.slug || app.id}`} className="text-muted-foreground hover:text-foreground text-sm transition-colors">{app.name}</Link>
                                </li>
                            )) : (
                                <li><Link to="/uygulamalar" className="text-muted-foreground hover:text-foreground text-sm transition-colors">Tum Uygulamalar</Link></li>
                            )}
                        </ul>
                    </div>
                </div>

                <div className="mt-12 pt-8 border-t border-border">
                    {/* Payment Logos - iyzico requirement */}
                    <div className="flex items-center justify-center gap-5 mb-6" data-testid="payment-logos">
                        <img src={isDark ? "/iyzico/footer-white.png" : "/iyzico/footer-colored.png"} alt="iyzico ile öde - Visa, Mastercard, Troy" className="h-7 opacity-80 hover:opacity-100 transition-opacity" />
                    </div>

                    <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mb-6">
                        <Link to="/mesafeli-satis-sozlesmesi" className="text-muted-foreground hover:text-foreground text-xs transition-colors" data-testid="footer-distance-sales">Mesafeli Satış Sözleşmesi</Link>
                        <Link to="/iptal-iade" className="text-muted-foreground hover:text-foreground text-xs transition-colors" data-testid="footer-cancellation">İptal ve İade Koşulları</Link>
                        <Link to="/gizlilik-politikasi" className="text-muted-foreground hover:text-foreground text-xs transition-colors" data-testid="footer-privacy">KVKK ve Gizlilik Politikası</Link>
                        <Link to="/hakkimizda" className="text-muted-foreground hover:text-foreground text-xs transition-colors" data-testid="footer-about">Hakkımızda</Link>
                        <Link to="/iletisim" className="text-muted-foreground hover:text-foreground text-xs transition-colors" data-testid="footer-contact">İletişim</Link>
                    </div>
                    <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                        <p className="text-muted-foreground text-sm">
                            &copy; {currentYear} {settings.company_name || 'MacTech'}. Tum haklari saklidir.
                        </p>
                        <div className="flex items-center gap-2 text-xs" data-testid="powered-by-mactech">
                            <span className="text-muted-foreground/70">Ti-Cari Ekosistemi</span>
                            <span className="text-muted-foreground/40">·</span>
                            <span className="inline-flex items-center gap-1.5 text-muted-foreground/70">
                                Powered by
                                <span className="font-semibold bg-gradient-to-r from-[#C5A267] via-[#D4B98D] to-[#A68A50] bg-clip-text text-transparent">MacTech</span>
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};
